// =============================================================================
// FollowupTab — de automatische mails na een bezoek
// =============================================================================
// De tegenhanger van de campagnes: die stuur je zelf naar een doelgroep, deze
// gaan vanzelf naar één bezoeker. Het zijn er twee, met elk een eigen klok:
//
//   Reviewmail  — naar iedereen die langs is geweest, met een link naar Google
//                 en een naar Tripadvisor. Allebei lopen ze via
//                 manage.neoseum.com, zodat het domein in de mail van onszelf
//                 is én we zien welke iemand volgt. Wie er één volgt, vragen we
//                 het nooit meer.
//   Suggestie   — alleen als er op die vestiging nog iets te doen valt, met
//                 precies één voorstel.
//
// Panelen:
//  1. Instellingen — per mail een schakelaar en een vertraging.
//  2. Mails — de lijst IS de volgorde van de suggesties. Zet een mail hoger en
//     die wordt eerder voorgesteld. Bewust los van de volgorde in
//     Setup > Experiences: dat is de volgorde in de winkel, en dat is een
//     andere beslissing.
//  3. Droogloop — wie zou er nu een mail krijgen, per soort, zonder te sturen.
//  4. Resultaten — per mail, per vestiging (elke vestiging is een eigen Google-
//     vermelding) en per experience die mensen déden.
//
// De inhoud gebruikt exact hetzelfde model als een campagne (per taal een
// variant, blokken of geplakte HTML), dus dezelfde editor en hetzelfde
// server-side voorbeeld.
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatVenueDateTime } from '../lib/time';
import type { BookingLocation } from '../lib/walkin-data';
import {
  createFollowupTemplate,
  deleteFollowupTemplate,
  followupStats,
  getFollowupSettings,
  listFollowupTemplates,
  previewFollowup,
  previewFollowupDue,
  reorderFollowupTemplates,
  saveFollowupSettings,
  saveFollowupTemplate,
  testSendFollowup,
  type FollowupCandidate,
  type FollowupError,
  type FollowupKind,
  type FollowupSettings,
  type FollowupStatRow,
  type FollowupStats,
  type FollowupTemplate,
  type VenueMissingReviewLinks,
} from '../api/followup';
import type { CampaignContent, CampaignLocale, CampaignVariant } from '../api/marketing';
import { BlocksEditor, ScaledEmailFrame } from './MarketingScreen';

const KIND_LABEL: Record<FollowupKind, string> = {
  review: 'Reviewmail',
  suggestion: 'Suggestiemail',
};

function errText(e: FollowupError | null): string {
  if (!e) return 'Onbekende fout';
  return e.message ? `${e.error}: ${e.message}` : e.error;
}

function emptyVariant(): CampaignVariant {
  return {
    subject: '',
    preheader: null,
    mode: 'blocks',
    blocks: [
      { type: 'heading', text: 'Hoi {{voornaam}},' },
      { type: 'paragraph', text: '' },
    ],
  };
}

// "336 uur" says nothing; "14 dagen" is what the person setting it is thinking.
function humanHours(h: number): string {
  if (h < 24) return `${h} uur`;
  const whole = Math.floor(h / 24);
  const rest = h - whole * 24;
  const dayWord = whole === 1 ? 'dag' : 'dagen';
  return rest === 0 ? `${whole} ${dayWord}` : `${whole} ${dayWord} en ${rest} uur`;
}

function templateLabel(t: FollowupTemplate, locations: BookingLocation[]): string {
  const base = t.kind === 'review'
    ? 'Reviewmail'
    : `Suggestie: ${t.experience_name ?? t.suggest_experience_id ?? '—'}`;
  if (!t.location_id) return base;
  const loc = locations.find((l) => l.id === t.location_id);
  return `${base} — alleen ${loc?.display_name ?? loc?.name ?? 'één vestiging'}`;
}

// ---------------------------------------------------------------------------
export function FollowupTab({ locations }: { locations: BookingLocation[] }) {
  const [settings, setSettings] = useState<FollowupSettings | null>(null);
  const [dueCounts, setDueCounts] = useState<Record<string, number>>({});
  const [missingReviewLinks, setMissingReviewLinks] = useState<VenueMissingReviewLinks[]>([]);
  const [fromAddress, setFromAddress] = useState('');
  const [templates, setTemplates] = useState<FollowupTemplate[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const [s, t] = await Promise.all([getFollowupSettings(), listFollowupTemplates()]);
    if (s.ok && s.data) {
      setSettings(s.data.settings);
      setDueCounts(s.data.due_counts);
      setMissingReviewLinks(s.data.venues_missing_review_links);
      setFromAddress(s.data.from_address);
    } else setError(errText(s.error));
    if (t.ok && t.data) setTemplates(t.data.templates);
    else if (!s.error) setError(errText(t.error));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (editing === null) {
      setLoading(true);
      void load();
    }
  }, [editing, load]);

  if (editing) {
    const tpl = templates.find((t) => t.id === editing);
    if (!tpl) {
      setEditing(null);
      return null;
    }
    return (
      <FollowupTemplateEditor
        template={tpl}
        label={templateLabel(tpl, locations)}
        locations={locations}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="marketing-followup">
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <p className="muted">Laden…</p> : null}

      {settings ? (
        <SettingsPanel
          settings={settings}
          dueCounts={dueCounts}
          missingReviewLinks={missingReviewLinks}
          fromAddress={fromAddress}
          onSaved={setSettings}
          onError={setError}
          onReload={() => void load()}
        />
      ) : null}

      {!loading ? (
        <TemplateListPanel
          templates={templates}
          locations={locations}
          onEdit={setEditing}
          onChanged={() => void load()}
          onError={setError}
        />
      ) : null}

      {!loading ? <DryRunPanel /> : null}
      {!loading ? <StatsPanel /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Instellingen
// ---------------------------------------------------------------------------
function SettingsPanel({
  settings,
  dueCounts,
  missingReviewLinks,
  fromAddress,
  onSaved,
  onError,
  onReload,
}: {
  settings: FollowupSettings;
  dueCounts: Record<string, number>;
  missingReviewLinks: VenueMissingReviewLinks[];
  fromAddress: string;
  onSaved: (s: FollowupSettings) => void;
  onError: (e: string | null) => void;
  onReload: () => void;
}) {
  const [draft, setDraft] = useState<FollowupSettings>(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(settings), [settings]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const save = async (patch?: Partial<FollowupSettings>) => {
    setSaving(true);
    onError(null);
    const res = await saveFollowupSettings({ ...draft, ...patch });
    setSaving(false);
    if (res.ok && res.data) {
      onSaved(res.data.settings);
      onReload();
    } else onError(errText(res.error));
  };

  const kindCard = (kind: FollowupKind) => {
    const liveOn = kind === 'review' ? settings.review_enabled : settings.suggestion_enabled;
    const hours = kind === 'review' ? draft.review_delay_hours : draft.suggestion_delay_hours;
    const field = kind === 'review' ? 'review_delay_hours' : 'suggestion_delay_hours';
    return (
      <div className="walkin-field" key={kind}>
        <span><strong>{KIND_LABEL[kind]}</strong></span>
        <p className="muted small">
          {kind === 'review'
            ? 'Naar iedereen die langs is geweest. Wie de reviewlink volgt, krijgt hem nooit meer.'
            : 'Alleen als er op die vestiging nog een experience open staat. Eén voorstel per mail.'}
        </p>
        <label className="walkin-field">
          <span>Verstuur na</span>
          <input
            type="number"
            min={1}
            max={2160}
            value={hours}
            onChange={(e) => setDraft({ ...draft, [field]: Number(e.target.value) || 1 })}
          />
          <small className="muted">uur na het bezoek — nu {humanHours(hours)}</small>
        </label>
        <p className="muted small">
          Nu klaar om verstuurd te worden: <strong>{dueCounts[kind] ?? 0}</strong>
        </p>
        <button
          className={liveOn ? 'btn-link voucher-deactivate' : 'walkin-primary-btn'}
          disabled={saving}
          onClick={() =>
            void save(
              kind === 'review'
                ? { review_enabled: !liveOn }
                : { suggestion_enabled: !liveOn },
            )}
        >
          {liveOn ? `${KIND_LABEL[kind]} uitzetten` : `${KIND_LABEL[kind]} aanzetten`}
        </button>
      </div>
    );
  };

  return (
    <section className="marketing-panel">
      <h2>Instellingen</h2>

      <div className="walkin-row">
        {kindCard('review')}
        {kindCard('suggestion')}
      </div>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Bezoek geldt als klaar na</span>
          <input
            type="number"
            min={0.5}
            max={24}
            step={0.5}
            value={draft.visit_timeout_hours}
            onChange={(e) =>
              setDraft({ ...draft, visit_timeout_hours: Number(e.target.value) || 3 })}
          />
          <small className="muted">
            uur na het inchecken. De wachtrij meldt niet wanneer iemand écht klaar is, dus
            hier rekenen we met een vaste tijd. Geldt voor beide mails.
          </small>
        </label>
        <label className="walkin-field">
          <span>Niet vaker dan eens per</span>
          <input
            type="number"
            min={0}
            max={365}
            value={draft.min_days_between_sends}
            onChange={(e) =>
              setDraft({ ...draft, min_days_between_sends: Number(e.target.value) || 0 })}
          />
          <small className="muted">dagen per klant, per soort mail</small>
        </label>
        <label className="walkin-field">
          <span>Mails per ronde</span>
          <input
            type="number"
            min={1}
            max={200}
            value={draft.per_tick}
            onChange={(e) => setDraft({ ...draft, per_tick: Number(e.target.value) || 20 })}
          />
          <small className="muted">elke 15 minuten, per soort</small>
        </label>
      </div>

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Google-icoon</span>
          <input
            type="text"
            value={draft.google_icon_url ?? ''}
            placeholder="https://… .png"
            onChange={(e) =>
              setDraft({ ...draft, google_icon_url: e.target.value || null })}
          />
          <small className="muted">
            Token <code>{'{{google icon}}'}</code>, voor op de reviewknop.
          </small>
        </label>
        <label className="walkin-field">
          <span>Tripadvisor-icoon</span>
          <input
            type="text"
            value={draft.tripadvisor_icon_url ?? ''}
            placeholder="https://… .png"
            onChange={(e) =>
              setDraft({ ...draft, tripadvisor_icon_url: e.target.value || null })}
          />
          <small className="muted">
            Token <code>{'{{trip icon}}'}</code>.
          </small>
        </label>
      </div>
      <p className="muted marketing-hint">
        Gebruik de officiële merkbestanden van Google en Tripadvisor, als <strong>PNG</strong>:
        AVIF en WebP blijven leeg in de mail. Eén paar voor het hele systeem — het
        Google-logo is overal hetzelfde. Leeg laten mag: de knop toont dan alleen tekst, wat
        sowieso gebeurt bij iedereen die afbeeldingen blokkeert.
      </p>

      <p className="muted marketing-hint">
        Alleen klanten met marketing opt-in, zonder bounce of klacht — dezelfde regel als bij
        campagnes. Afzender: <strong>{fromAddress || '—'}</strong>.
      </p>

      {settings.active_from ? (
        <p className="muted marketing-hint">
          Aan sinds {formatVenueDateTime(settings.active_from)}. Bezoeken van vóór dat moment
          krijgen nooit een mail.
        </p>
      ) : (
        <p className="muted marketing-hint">
          Nog nooit aangezet. Zodra je de eerste mail aanzet tellen alleen bezoeken vanaf dat
          moment mee, dus oude bezoekers krijgen niet alsnog post.
        </p>
      )}

      {missingReviewLinks.length > 0 ? (
        <div className="error-card">
          Reviewlink ontbreekt:{' '}
          <strong>
            {missingReviewLinks
              .map((v) => `${v.name} (${v.missing.join(' + ')})`)
              .join(', ')}
          </strong>
          . Elke vestiging is een eigen vermelding op allebei de sites, dus dit staat per
          vestiging in Setup → Vestigingen. Zonder link komt de klant op neoseum.com uit in
          plaats van op het reviewformulier — haal die knop dan uit de mail.
        </div>
      ) : null}

      <div className="marketing-actions">
        <button className="btn-link" disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? 'Opslaan…' : 'Instellingen opslaan'}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2. Mails — de lijst IS de volgorde van de suggesties
// ---------------------------------------------------------------------------
function TemplateListPanel({
  templates,
  locations,
  onEdit,
  onChanged,
  onError,
}: {
  templates: FollowupTemplate[];
  locations: BookingLocation[];
  onEdit: (id: string) => void;
  onChanged: () => void;
  onError: (e: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newKind, setNewKind] = useState<FollowupKind>('suggestion');
  const [newExp, setNewExp] = useState('');
  const [newLoc, setNewLoc] = useState('');

  const reviews = templates.filter((t) => t.kind === 'review');
  const suggestions = templates.filter((t) => t.kind === 'suggestion');

  // Only the suggestions have an order; the review mail does not compete with
  // them, so it never gets arrows.
  const move = async (index: number, delta: number) => {
    const next = [...suggestions];
    const target = index + delta;
    const a = next[index];
    const b = next[target];
    if (!a || !b) return; // buiten de lijst — de knop staat dan al uit
    next[index] = b;
    next[target] = a;
    setBusy(true);
    onError(null);
    const res = await reorderFollowupTemplates(next.map((t) => t.id));
    setBusy(false);
    if (res.ok) onChanged();
    else onError(errText(res.error));
  };

  const add = async () => {
    setBusy(true);
    onError(null);
    const res = await createFollowupTemplate({
      kind: newKind,
      suggest_experience_id: newKind === 'suggestion' ? (newExp || null) : null,
      location_id: newLoc || null,
    });
    setBusy(false);
    if (res.ok) {
      setAdding(false);
      setNewExp('');
      setNewLoc('');
      onChanged();
    } else onError(errText(res.error));
  };

  const remove = async (t: FollowupTemplate) => {
    if (!confirm(`Deze mail verwijderen? (${templateLabel(t, locations)})`)) return;
    setBusy(true);
    const res = await deleteFollowupTemplate(t.id);
    setBusy(false);
    if (res.ok) onChanged();
    else onError(errText(res.error));
  };

  const row = (t: FollowupTemplate, index: number | null) => (
    <tr key={t.id} className="marketing-row" onClick={() => onEdit(t.id)}>
      <td>
        {index != null ? (
          <span className="marketing-block-tools" onClick={(e) => e.stopPropagation()}>
            <button className="btn-link" disabled={busy || index === 0} onClick={() => void move(index, -1)}>
              ↑
            </button>
            <button
              className="btn-link"
              disabled={busy || index === suggestions.length - 1}
              onClick={() => void move(index, 1)}
            >
              ↓
            </button>
          </span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
      <td><strong>{templateLabel(t, locations)}</strong></td>
      <td className="muted">
        {t.locales.length > 0 ? t.locales.map((l) => l.toUpperCase()).join(' + ') : 'leeg'}
      </td>
      <td>
        <span className={t.enabled ? 'pill marketing-pill-sent' : 'pill marketing-pill-draft'}>
          {t.enabled ? 'Actief' : 'Uit'}
        </span>
      </td>
      <td className="muted">{formatVenueDateTime(t.updated_at)}</td>
      <td onClick={(e) => e.stopPropagation()}>
        {t.location_id ? (
          <button className="btn-link voucher-deactivate" disabled={busy} onClick={() => void remove(t)}>
            Verwijder
          </button>
        ) : null}
      </td>
    </tr>
  );

  return (
    <section className="marketing-panel">
      <h2>Mails</h2>
      <p className="muted marketing-hint">
        De reviewmail gaat naar iedereen. Voor de suggesties is de volgorde van deze lijst de
        volgorde waarin we voorstellen: de bovenste mail waarvan de klant die experience hier
        nog niet deed, wint. Een mail die uit staat of nog leeg is, wordt overgeslagen — dan
        krijgt die klant geen suggestie.
      </p>
      <table className="vouchers-table marketing-table">
        <thead>
          <tr>
            <th>Volgorde</th>
            <th>Mail</th>
            <th>Talen</th>
            <th>Status</th>
            <th>Gewijzigd</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((t) => row(t, null))}
          {suggestions.map((t, i) => row(t, i))}
        </tbody>
      </table>

      {adding ? (
        <div className="walkin-row">
          <label className="walkin-field">
            <span>Soort</span>
            <select value={newKind} onChange={(e) => setNewKind(e.target.value as FollowupKind)}>
              <option value="suggestion">Suggestiemail</option>
              <option value="review">Reviewmail</option>
            </select>
          </label>
          {newKind === 'suggestion' ? (
            <label className="walkin-field">
              <span>Suggestie</span>
              <select value={newExp} onChange={(e) => setNewExp(e.target.value)}>
                <option value="">Kies een experience…</option>
                {suggestions
                  .filter((t) => t.location_id === null && t.suggest_experience_id)
                  .map((t) => (
                    <option key={t.id} value={t.suggest_experience_id as string}>
                      {t.experience_name ?? t.suggest_experience_id}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
          <label className="walkin-field">
            <span>Alleen voor vestiging</span>
            <select value={newLoc} onChange={(e) => setNewLoc(e.target.value)}>
              <option value="">Alle vestigingen</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
              ))}
            </select>
          </label>
          <div className="marketing-actions">
            <button
              className="walkin-primary-btn"
              disabled={busy || (newKind === 'suggestion' && !newExp)}
              onClick={() => void add()}
            >
              Toevoegen
            </button>
            <button className="btn-link" onClick={() => setAdding(false)}>Annuleer</button>
          </div>
        </div>
      ) : (
        <button className="btn-link" onClick={() => setAdding(true)}>
          + Mail voor één vestiging
        </button>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Editor voor één mail
// ---------------------------------------------------------------------------
function FollowupTemplateEditor({
  template,
  label,
  locations,
  onClose,
}: {
  template: FollowupTemplate;
  label: string;
  locations: BookingLocation[];
  onClose: () => void;
}) {
  const [content, setContent] = useState<CampaignContent>(
    Object.keys(template.content ?? {}).length > 0 ? template.content : { nl: emptyVariant() },
  );
  const [defaultLocale, setDefaultLocale] = useState<CampaignLocale>(template.default_locale);
  const [lang, setLang] = useState<CampaignLocale>(template.default_locale);
  const [testTo, setTestTo] = useState('');
  // Which venue's review links the preview and the test mail should use. Each
  // venue is its own listing on both sites, so "does the link work" is a
  // question per venue. Empty = the server picks the first venue that has any
  // link configured.
  const [testVenue, setTestVenue] = useState('');
  const [linkNote, setLinkNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const variant = content[lang] ?? emptyVariant();
  const setVariant = (v: CampaignVariant) => setContent({ ...content, [lang]: v });
  const hasNl = !!content.nl;
  const hasEn = !!content.en;

  const save = async (patch?: { enabled?: boolean }) => {
    setBusy('save');
    setError(null);
    setNote(null);
    const res = await saveFollowupTemplate(template.id, {
      content,
      default_locale: defaultLocale,
      ...patch,
    });
    setBusy(null);
    if (res.ok) setNote('Opgeslagen.');
    else setError(errText(res.error));
    return res.ok;
  };

  const test = async () => {
    // Save first: a test of stale content proves nothing.
    if (!(await save())) return;
    setBusy('test');
    setError(null);
    const res = await testSendFollowup(
      template.id,
      lang,
      testTo.trim() || undefined,
      undefined,
      testVenue || null,
    );
    setBusy(null);
    if (res.ok && res.data) {
      const who = res.data.sample_names
        ? `${res.data.to} (met voorbeeldnaam — dit adres is geen klant)`
        : res.data.to;
      const missing = [
        res.data.has_google_review_url ? null : 'Google',
        res.data.has_tripadvisor_review_url ? null : 'Tripadvisor',
      ].filter(Boolean);
      setNote(
        `Testmail naar ${who}. Reviewlinks van ${res.data.venue ?? 'onbekende vestiging'}` +
          (missing.length > 0 ? ` — geen ${missing.join(' en ')}-link ingesteld, die knop valt weg.` : '.'),
      );
    } else setError(errText(res.error));
  };

  return (
    <div className="marketing-followup">
      <div className="vouchers-toolbar">
        <p className="muted"><strong>{label}</strong></p>
        <div className="vouchers-toolbar-actions">
          <button className="btn-link" onClick={onClose}>← Terug</button>
        </div>
      </div>

      <section className="marketing-panel">
        <div className="marketing-lang-tabs">
          {hasNl ? (
            <button
              className={lang === 'nl' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('nl')}
            >
              NL{defaultLocale === 'nl' ? ' (standaard)' : ''}
            </button>
          ) : null}
          {hasEn ? (
            <button
              className={lang === 'en' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setLang('en')}
            >
              EN{defaultLocale === 'en' ? ' (standaard)' : ''}
            </button>
          ) : null}
          {!hasEn ? (
            <button
              className="btn-link"
              onClick={() => { setContent({ ...content, en: emptyVariant() }); setLang('en'); }}
            >
              + Engelse variant
            </button>
          ) : null}
          {!hasNl ? (
            <button
              className="btn-link"
              onClick={() => { setContent({ ...content, nl: emptyVariant() }); setLang('nl'); }}
            >
              + Nederlandse variant
            </button>
          ) : null}
          {hasNl && hasEn ? (
            <button
              className="btn-link voucher-deactivate"
              onClick={() => {
                const next = { ...content };
                delete next[lang];
                const remaining: CampaignLocale = lang === 'nl' ? 'en' : 'nl';
                setContent(next);
                setDefaultLocale(remaining);
                setLang(remaining);
              }}
            >
              {lang === 'nl' ? 'NL-variant verwijderen' : 'EN-variant verwijderen'}
            </button>
          ) : null}
        </div>

        <p className="muted marketing-hint">
          Tokens: <code>{'{{voornaam}}'}</code> <code>{'{{achternaam}}'}</code>{' '}
          <code>{'{{email}}'}</code> <code>{'{{unsubscribe_url}}'}</code>{' '}
          <code>{'{{google review}}'}</code> <code>{'{{trip review}}'}</code>{' '}
          <code>{'{{google icon}}'}</code> <code>{'{{trip icon}}'}</code>{' '}
          <code>{'{{vestiging}}'}</code> <code>{'{{bezochte_experience}}'}</code>
          {template.kind === 'suggestion' ? (
            <>
              {' '}<code>{'{{experience_naam}}'}</code>{' '}
              <code>{'{{experience_beschrijving}}'}</code>
            </>
          ) : null}
          . Hoofdletters, spaties en underscores maken niet uit:{' '}
          <code>{'{{Google review}}'}</code> en <code>{'{{google_review}}'}</code> werken
          allebei.
        </p>
        <p className="muted marketing-hint">
          <strong>De reviewlinks</strong> wijzen naar manage.neoseum.com en sturen door naar
          de Google- of Tripadvisor-pagina van de vestiging waar de klant was — elke
          vestiging is op allebei de sites een eigen vermelding, in te stellen bij
          Setup → Vestigingen. Dat de link van ons eigen domein komt is niet alleen netter
          voor de bezorging: het is ook hoe we zien wie erop klikt, zodat we diegene daarna
          niet nog eens om een review vragen. Heeft een vestiging geen link voor een site,
          dan blijft dat token leeg en valt een knop met die link vanzelf weg bij die
          ontvanger — je hoeft dus geen aparte mail per vestiging te maken.
        </p>
        <p className="muted marketing-hint">
          <strong>Een reviewknop maken:</strong> in Blokken-modus voeg je een Knop toe, zet
          je <code>{'{{google review}}'}</code> in het linkveld en eventueel{' '}
          <code>{'{{google icon}}'}</code> in het icoonveld. In HTML-modus zet je het token
          in de <code>href</code> en plaats je zelf een <code>&lt;img&gt;</code> in de link.
        </p>
        <p className="muted marketing-hint">
          <strong>Afbeeldingen:</strong> zet die zelf in deze mail, en gebruik{' '}
          <strong>JPEG of PNG</strong>. De plaatjes van de website zijn AVIF en WebP: die zijn
          goed voor de site, maar blijven leeg in de mail — AVIF in zo goed als elke
          mailclient, WebP in Outlook op Windows. Controleer het met een testmail voordat je
          deze mail aanzet.
        </p>

        <div className="marketing-editor-grid">
          <div className="marketing-editor-col">
            <label className="walkin-field">
              <span>Onderwerp ({lang.toUpperCase()})</span>
              <input
                type="text"
                value={variant.subject}
                onChange={(e) => setVariant({ ...variant, subject: e.target.value })}
                placeholder={
                  template.kind === 'review'
                    ? 'Hoe was het bij Neoseum?'
                    : 'Klaar voor de volgende?'
                }
              />
            </label>
            <label className="walkin-field">
              <span>Preheader (voorbeeldtekst naast het onderwerp, optioneel)</span>
              <input
                type="text"
                value={variant.preheader ?? ''}
                onChange={(e) => setVariant({ ...variant, preheader: e.target.value || null })}
              />
            </label>

            <div className="marketing-mode-toggle">
              <button
                className={variant.mode === 'blocks' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'blocks', blocks: variant.blocks ?? emptyVariant().blocks })}
              >
                Blokken
              </button>
              <button
                className={variant.mode === 'html' ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
                onClick={() => setVariant({ ...variant, mode: 'html', html: variant.html ?? '' })}
              >
                HTML plakken
              </button>
            </div>

            {variant.mode === 'blocks' ? (
              <BlocksEditor
                blocks={variant.blocks ?? []}
                onChange={(blocks) => setVariant({ ...variant, blocks })}
              />
            ) : (
              <>
                <textarea
                  className="marketing-html-input"
                  rows={16}
                  value={variant.html ?? ''}
                  placeholder="<html>…</html> — plak hier de volledige e-mail-HTML"
                  onChange={(e) => setVariant({ ...variant, html: e.target.value })}
                  spellCheck={false}
                />
                <p className="muted marketing-hint">
                  Zonder <code>{'{{unsubscribe_url}}'}</code> in je HTML wordt de standaard
                  footer (uitschrijflink + adres) automatisch onderaan toegevoegd.
                </p>
              </>
            )}
          </div>

          <FollowupPreviewPane
            content={content}
            lang={lang}
            defaultLocale={defaultLocale}
            suggestExperienceId={template.suggest_experience_id}
            locationId={testVenue || null}
            onLinkContext={setLinkNote}
          />
        </div>
      </section>

      {error ? <div className="error-card">{error}</div> : null}
      {note ? <p className="muted">{note}</p> : null}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Testmail naar</span>
          <input
            type="email"
            placeholder="leeg = jezelf"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
          />
        </label>
        <label className="walkin-field">
          <span>Reviewlinks van welke vestiging</span>
          <select value={testVenue} onChange={(e) => setTestVenue(e.target.value)}>
            <option value="">Eerste vestiging met links</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.display_name ?? l.name}</option>
            ))}
          </select>
          <small className="muted">
            Geldt voor het voorbeeld hierboven én voor de testmail.
          </small>
        </label>
      </div>

      <div className="marketing-actions">
        <button className="btn-link" disabled={busy != null} onClick={() => void test()}>
          {busy === 'test' ? 'Versturen…' : 'Testmail versturen'}
        </button>
        <button className="btn-link" disabled={busy != null} onClick={() => void save()}>
          {busy === 'save' ? 'Opslaan…' : 'Opslaan'}
        </button>
        <button
          className={template.enabled ? 'btn-link voucher-deactivate' : 'walkin-primary-btn'}
          disabled={busy != null}
          onClick={async () => {
            if (await save({ enabled: !template.enabled })) onClose();
          }}
        >
          {template.enabled ? 'Uitzetten' : 'Opslaan en aanzetten'}
        </button>
      </div>
      {linkNote ? <p className="muted marketing-hint">{linkNote}</p> : null}
      {template.kind === 'review' ? (
        <p className="muted marketing-hint">
          In het voorbeeld en de testmail staan de <strong>echte</strong> reviewlinks van de
          gekozen vestiging, dus je kunt erop klikken om te controleren of ze op de juiste
          pagina uitkomen. Het zijn de directe links naar Google en Tripadvisor, niet de
          omweg via manage.neoseum.com die de echte mail gebruikt: die omweg hoort bij één
          verzending, en een testmail maakt er geen. Voordeel: jouw klik telt nergens mee en
          markeert je niet als "heeft al gereviewd".
        </p>
      ) : null}
    </div>
  );
}

// Same contract as the campaign preview: the SERVER renders exactly what a
// real send produces, including the auto-appended footer and the experience
// copy for this template's suggestion.
function FollowupPreviewPane({
  content,
  lang,
  defaultLocale,
  suggestExperienceId,
  locationId,
  onLinkContext,
}: {
  content: CampaignContent;
  lang: CampaignLocale;
  defaultLocale: CampaignLocale;
  suggestExperienceId: string | null;
  locationId: string | null;
  onLinkContext: (note: string | null) => void;
}) {
  const [html, setHtml] = useState('');
  const [text, setText] = useState('');
  const [subject, setSubject] = useState('');
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [tab, setTab] = useState<'render' | 'text' | 'raw'>('render');
  const seq = useRef(0);

  useEffect(() => {
    const v = content[lang];
    if (!v || !v.subject.trim()) {
      setState('idle');
      setHtml('');
      setText('');
      setSubject('');
      return;
    }
    setState('loading');
    const mySeq = ++seq.current;
    const t = setTimeout(async () => {
      const res = await previewFollowup(
        content, lang, defaultLocale, suggestExperienceId, locationId,
      );
      if (seq.current !== mySeq) return; // verouderd antwoord
      if (res.ok && res.data) {
        setHtml(res.data.html);
        setText(res.data.text);
        setSubject(res.data.subject);
        setState('idle');
        const missing = [
          res.data.has_google_review_url ? null : 'Google',
          res.data.has_tripadvisor_review_url ? null : 'Tripadvisor',
        ].filter(Boolean);
        onLinkContext(
          res.data.venue
            ? `Voorbeeld toont de reviewlinks van ${res.data.venue}` +
              (missing.length > 0
                ? ` — geen ${missing.join(' en ')}-link ingesteld, die knop valt weg.`
                : '.')
            : null,
        );
      } else setState('error');
    }, 600);
    return () => clearTimeout(t);
  }, [content, lang, defaultLocale, suggestExperienceId, locationId, onLinkContext]);

  return (
    <div className="marketing-preview">
      <div className="marketing-preview-head">
        <span className="marketing-block-type">Voorbeeld ({lang.toUpperCase()})</span>
        <span className="marketing-block-tools">
          {state === 'loading' ? <span className="muted small">renderen…</span> : null}
          {state === 'error' ? <span className="muted small">voorbeeld niet beschikbaar</span> : null}
          {(['render', 'text', 'raw'] as const).map((t) => (
            <button
              key={t}
              className={tab === t ? 'marketing-lang-tab marketing-lang-tab-active' : 'marketing-lang-tab'}
              onClick={() => setTab(t)}
            >
              {t === 'render' ? 'Weergave' : t === 'text' ? 'Tekstversie' : 'Broncode'}
            </button>
          ))}
        </span>
      </div>
      {subject ? <div className="marketing-preview-subject">{subject}</div> : null}
      {html && tab === 'render' ? <ScaledEmailFrame html={html} /> : null}
      {html && tab === 'text' ? (
        <pre className="marketing-preview-raw marketing-preview-text">{text}</pre>
      ) : null}
      {html && tab === 'raw' ? <pre className="marketing-preview-raw">{html}</pre> : null}
      {!html ? (
        <div className="marketing-preview-empty muted">
          Vul onderwerp en inhoud in om het voorbeeld te zien.
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Droogloop
// ---------------------------------------------------------------------------
// Wat de eerstvolgende ronde zou doen, zonder het te doen. Lees dit vóór je
// een schakelaar omzet: het is de enige manier om te zien of het klopt tegen
// echte bezoeken.
function DryRunPanel() {
  const [kind, setKind] = useState<FollowupKind>('review');
  const [rows, setRows] = useState<FollowupCandidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (k: FollowupKind) => {
    setKind(k);
    setBusy(true);
    setError(null);
    setRows(null);
    const res = await previewFollowupDue(k, 25);
    setBusy(false);
    if (res.ok && res.data) setRows(res.data.candidates);
    else setError(errText(res.error));
  };

  return (
    <section className="marketing-panel">
      <h2>Droogloop</h2>
      <p className="muted marketing-hint">
        Wie zou er nu een mail krijgen, en wat zouden we voorstellen? Er wordt niets
        verstuurd.
      </p>
      <div className="marketing-actions">
        {(['review', 'suggestion'] as const).map((k) => (
          <button key={k} className="btn-link" disabled={busy} onClick={() => void run(k)}>
            {busy && kind === k ? 'Kijken…' : `Bekijk ${KIND_LABEL[k].toLowerCase()}`}
          </button>
        ))}
      </div>
      {error ? <div className="error-card">{error}</div> : null}
      {rows && rows.length === 0 ? (
        <p className="muted">Voor de {KIND_LABEL[kind].toLowerCase()} staat er niemand klaar.</p>
      ) : null}
      {rows && rows.length > 0 ? (
        <table className="vouchers-table marketing-table">
          <thead>
            <tr>
              <th>Klant</th>
              <th>Vestiging</th>
              <th>Bezocht</th>
              {kind === 'suggestion' ? <th>Suggestie</th> : null}
              <th>Bezoek klaar</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.departure_id}>
                <td>{c.first_name ?? '—'} <span className="muted small">{c.email}</span></td>
                <td className="muted">{c.location_name ?? '—'}</td>
                <td className="muted">{c.visited.join(', ') || '—'}</td>
                {kind === 'suggestion' ? <td>{c.suggestion ?? '—'}</td> : null}
                <td className="muted">{formatVenueDateTime(c.visit_ended_at)}</td>
                <td>
                  {!c.would_send ? (
                    <span className="muted small">geen mail geschreven</span>
                  ) : kind === 'review' && !c.has_google_review_url && !c.has_tripadvisor_review_url ? (
                    <span className="muted small">vestiging heeft geen reviewlinks</span>
                  ) : kind === 'review' && !c.has_google_review_url ? (
                    <span className="muted small">geen Google-link</span>
                  ) : kind === 'review' && !c.has_tripadvisor_review_url ? (
                    <span className="muted small">geen Tripadvisor-link</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4. Resultaten
// ---------------------------------------------------------------------------
function StatsPanel() {
  const [stats, setStats] = useState<FollowupStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void followupStats().then((res) => {
      if (cancelled) return;
      if (res.ok && res.data) setStats(res.data);
      else setError(errText(res.error));
    });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <section className="marketing-panel"><div className="error-card">{error}</div></section>;
  }
  if (!stats) {
    return (
      <section className="marketing-panel">
        <h2>Resultaten</h2>
        <p className="muted">Laden…</p>
      </section>
    );
  }

  const total = stats.by_mail.reduce((n, r) => n + r.sent, 0);
  if (total === 0) {
    return (
      <section className="marketing-panel">
        <h2>Resultaten</h2>
        <p className="muted">Nog niets verstuurd.</p>
      </section>
    );
  }

  const table = (title: string, hint: string, rows: FollowupStatRow[]) => (
    <>
      <h3>{title}</h3>
      <p className="muted marketing-hint">{hint}</p>
      <table className="vouchers-table marketing-table">
        <thead>
          <tr>
            <th></th>
            <th>Verzonden</th>
            <th>Bezorgd</th>
            <th>Geopend</th>
            <th>Review geklikt</th>
            <th>Google</th>
            <th>Tripadvisor</th>
            <th>Overgeslagen</th>
            <th>Mislukt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>
                <strong>{r.label}</strong>
                {r.has_google_review_url === false || r.has_tripadvisor_review_url === false ? (
                  <div className="muted small">
                    geen link voor{' '}
                    {[
                      r.has_google_review_url === false ? 'Google' : null,
                      r.has_tripadvisor_review_url === false ? 'Tripadvisor' : null,
                    ].filter(Boolean).join(' + ')}
                  </div>
                ) : null}
              </td>
              <td>{r.sent}</td>
              <td>{r.delivered}</td>
              <td>{r.opened}</td>
              <td>
                <strong>{r.review_clicks}</strong>
                {r.sent > 0 ? (
                  <span className="muted small">
                    {' '}({Math.round((r.review_clicks / r.sent) * 100)}%)
                  </span>
                ) : null}
              </td>
              <td className="muted">{r.review_clicks_google}</td>
              <td className="muted">{r.review_clicks_tripadvisor}</td>
              <td className="muted">{r.skipped}</td>
              <td className={r.failed > 0 ? 'voucher-deactivate' : 'muted'}>{r.failed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <section className="marketing-panel">
      <h2>Resultaten</h2>
      {table('Per mail', 'Hoe doet elke mail het?', stats.by_mail)}
      {table(
        'Per vestiging',
        'Elke vestiging is een eigen vermelding op allebei de sites, dus dit zegt of die vermeldingen groeien — en welke van de twee mensen kiezen.',
        stats.by_venue,
      )}
      {stats.by_visited_experience.length > 0
        ? table(
          'Per experience die ze déden',
          'Gegroepeerd op wat de klant beleefde, niet op wat we voorstelden: dit beantwoordt welke experience mensen aanzet tot een review. Wie er twee deed telt bij allebei.',
          stats.by_visited_experience,
        )
        : null}
    </section>
  );
}
